import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { WorkspaceInfo } from '@/lib/workspace/types/types'

interface WorkspaceSelectionState {
  workspaces: WorkspaceInfo[]
  activeWorkspaceId: string | null
  /**
   * Has refreshFromServer completed at least once (success OR failure)?
   *
   * The boot state machine (apps/lumen/src/app/bootState.ts) reads this to
   * distinguish "workspaces array is empty because we haven't fetched yet"
   * (state: workspace-pending) from "workspaces array is empty because the
   * server returned []" (state: onboarding-required). The flag intentionally
   * flips true on errors too — we still know we tried.
   */
  bootstrapped: boolean

  setWorkspaces: (list: WorkspaceInfo[]) => void
  selectWorkspace: (workspaceId: string) => void
  getActiveWorkspace: () => WorkspaceInfo | null
  /**
   * High-level orchestrated workspace switch. Coordinates:
   *   1. Best-effort drain of pending ops
   *   2. Committing the selection change
   *   3. Swapping the per-workspace IDB store
   *   4. Forcing WS reconnect (AUTH frame picks up new workspaceId)
   *   5. Navigating to /{workspaceId}
   *
   * UI surfaces should call this instead of selectWorkspace() directly.
   * selectWorkspace() is kept as the low-level synchronous setter used
   * internally by the orchestrator and refreshFromServer().
   */
  switchToWorkspace: (workspaceId: string, navigate: (path: string) => void) => Promise<void>
  /**
   * Refresh the workspaces list from the server. Called after login,
   * after creating a new workspace, after accepting an invite, etc.
   */
  refreshFromServer: () => Promise<void>
  /**
   * Reset all workspace state (workspaces, activeWorkspaceId, bootstrapped).
   * Called from useAuthStore.logout() so the next sign-in starts from a
   * clean boot-state-machine state instead of inheriting the previous
   * user's bootstrapped=true.
   */
  resetForLogout: () => void
}

export const useWorkspaceStore = create<WorkspaceSelectionState>()(
  persist(
    (set, get) => ({
      workspaces: [],
      activeWorkspaceId: null,
      bootstrapped: false,

      setWorkspaces: (list) => set({ workspaces: list }),

      selectWorkspace: (workspaceId) => {
        const exists = get().workspaces.some((w) => w.id === workspaceId)
        if (!exists) {
          throw new Error(`selectWorkspace: workspaceId '${workspaceId}' not in workspaces list`)
        }
        // Entity-store activation is owned by SyncSystemProvider — when
        // activeWorkspaceId changes, the provider's useMemo dep on
        // workspaceId triggers a remount that calls setActiveEntityStore.
        // The render tree above the provider (MainLayout) gates the
        // provider on activeWorkspaceId being non-null.
        set({ activeWorkspaceId: workspaceId })
      },

      switchToWorkspace: async (workspaceId, navigate) => {
        // Dynamic import breaks the circular dep: workspaceSwitch imports
        // useWorkspaceStore; if useWorkspaceStore imported
        // workspaceSwitch at module-load time, that would form a static cycle.
        // vi.mock() in Vitest intercepts dynamic imports correctly (≥1.0).
        const { switchToWorkspace } = await import('@/lib/workspace/utils/workspace-switch')
        await switchToWorkspace(workspaceId, { navigate })
      },

      getActiveWorkspace: () => {
        const { workspaces, activeWorkspaceId } = get()
        if (!activeWorkspaceId) return null
        return workspaces.find((w) => w.id === activeWorkspaceId) ?? null
      },

      refreshFromServer: async () => {
        // Dynamic import to break the static cycle: `./api` imports from
        // `@adapters/http/client`, which might transitively import store
        // bootstrap code. The dynamic form is fine at runtime but tests
        // that vi.mock('./api') need vi.doMock or the standard static-mock
        // hoist — vi.mock at the top of a test file intercepts dynamic
        // imports correctly in modern Vitest (≥1.0) because Vite hoists
        // the mock to the module registry.
        const { listWorkspaces } = await import('@/lib/workspace/api/api')
        const list = await listWorkspaces()
        set({ workspaces: list })

        // Default-to-personal handling. Two cases collapse into one:
        //   (a) No selection yet (first login)
        //   (b) Selection points to a workspace the user no longer belongs to
        //       (removed by an admin, workspace deleted). Without this guard,
        //       activeWorkspaceId persists pointing at a ghost ID — split state
        //       where getActiveWorkspace() returns null but activeWorkspaceId
        //       is non-null. Routing logic comparing the two handles it
        //       inconsistently.
        const { activeWorkspaceId } = get()
        const stillValid =
          activeWorkspaceId !== null && list.some((w) => w.id === activeWorkspaceId)
        if (!stillValid) {
          const personal = list.find((w) => w.type === 'personal')
          set({ activeWorkspaceId: personal ? personal.id : null })
        }

        // Mark bootstrapped ONLY on success. A transient error (network blip,
        // mid-rotation 401) should leave the flag false so the boot state
        // machine stays in 'workspace-pending' rather than incorrectly
        // deriving 'onboarding-required' from `bootstrapped + empty list`.
        // WorkspaceBootstrap's caller can retry. On legitimate empty result
        // (server returned []), the assignment above leaves workspaces = []
        // and bootstrapped flips to true — boot state correctly derives
        // 'onboarding-required'.
        set({ bootstrapped: true })
      },

      resetForLogout: () => {
        set({ workspaces: [], activeWorkspaceId: null, bootstrapped: false })
      },
    }),
    {
      name: 'nula-workspace',
      // Persist activeWorkspaceId across reloads. We re-fetch `workspaces` from
      // server on app init, so storing the full list is optional. Persisting it
      // gives a fast first paint; refreshFromServer overwrites with fresh data.
      partialize: (state) => ({
        activeWorkspaceId: state.activeWorkspaceId,
        workspaces: state.workspaces,
      }),
    },
  ),
)

